import _ from 'underscore';

import { Location } from '~src/commonInfra/utils/Location';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { BasePathsUtils } from '../../../bootstrap/routerHelpers/BasePathsUtils';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { SiteUrlNameCapturingRegex } from './SiteUrlMatcherFactory';

export namespace StartPageService {

  // Returns the default server start page when none has been set.
  export function getServerDefaultStartPage(): string {
    return '/';
  }

  // Given a startPageUrl and a siteUrlName, returns the navigable start page. i.e.:
  //  1. If start page url is invalid, returns default start page for current site
  //  2. Prepends site prefix to the startPageUrl if site prefix is needed (not needed for default site
  //     urls and for non-site urls)
  export function getStartPageForSite(startPageUrl: string, siteUrlName: string): string {
    const isValidUrl = isValidPath(startPageUrl);
    const isDefaultSite = !siteUrlName;
    if (!isValidUrl) {
      return getDefaultStartPageForSite(siteUrlName);
    } else if (isSitePath(startPageUrl) && !isDefaultSite) {
      return '/site/' + siteUrlName + startPageUrl;
    } else {
      return startPageUrl;
    }
  }

  export function isValidPath(path: string): boolean {
    // the path being passed in doesn't include the /site/sitename prefix
    // so verify paths against urls without a site name.
    const urls = new SpacesSiteUrls({siteUrlName: ''});
    const sitePaths = BasePathsUtils.getWatchedBasePathsForSite(urls);
    const allBasePaths = sitePaths.concat(BasePathsUtils.getWatchedBasePathsForServer());
    return path !== '/' && _.some(allBasePaths, (basePath) => {
      return path.startsWith(basePath);
    });
  }

  export function isSitePath(path: string): boolean {
    return isValidPath(path) && !path.startsWith('/server/');
  }

  // Gets a navigable start page url for the current user on the given site
  export function getCurrentUserStartPageForSite(siteUrlName: string): string {
    return getStartPageForSite(getSessionManagementServiceInstance().getStartPage(), siteUrlName);
  }

  // Gets the navigable start page url for the current user on the current site
  export function getCurrentUserStartPageForCurrentSite(): string {
    const server = getSessionManagementServiceInstance();
    return getStartPageForSite(server.getStartPage(), server.sessionSiteUrlName());
  }

  // Get the current location that's after the site prefix, e.g., if the current page is
  // https://qa-near/#/site/testsite/favorites, return /favorites
  export function getCurrentUrlAsStartPage(): string {
    return Location.instance.hash().slice(1).replace(SiteUrlNameCapturingRegex, '');
  }

  export function getDefaultStartPageForSite(siteUrlName: string): string {
    const siteUrls = new SpacesSiteUrls({ siteUrlName });
    return siteUrls.contentRootPath().replace(/^#/, '');
  }

}

export type StartPageService = typeof StartPageService;
